import { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, Tag, Check, X, RefreshCw, Search, Filter, 
  AlertTriangle, Edit2, Trash2, Save, CheckCircle, XCircle,
  ChevronDown, Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MenuOverridesPage() {
  // State
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOverridden, setFilterOverridden] = useState('all'); // all, overridden, no-override
  
  // Dialog state
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    priceOverrideCents: null,
    isAvailableOverride: null
  });
  const [saving, setSaving] = useState(false);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('backoffice_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Merchant-ID': localStorage.getItem('backoffice_active_restaurant') || ''
    };
  };

  // Fetch branches
  const fetchBranches = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/branches`, { headers: getHeaders() });
      const data = await res.json();
      setBranches(data.branches || []);
      
      // Set default branch
      const savedBranchId = localStorage.getItem('current_branch_id');
      const savedBranch = savedBranchId 
        ? data.branches?.find(b => b.id.toString() === savedBranchId)
        : null;
      
      if (savedBranch) {
        setSelectedBranch(savedBranch);
      } else if (data.branches?.length > 0) {
        setSelectedBranch(data.branches[0]);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
      toast.error('Failed to load branches');
    }
  }, []);

  // Fetch menu items with overrides
  const fetchItems = useCallback(async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/menu/items-with-overrides?branchId=${selectedBranch.id}`,
        { headers: getHeaders() }
      );
      
      if (!res.ok) throw new Error('Failed to fetch menu items');
      
      const data = await res.json();
      setItems(data.items || []);
      setFilteredItems(data.items || []);
    } catch (err) {
      console.error('Error fetching items:', err);
      toast.error('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Filter items
  useEffect(() => {
    let result = [...items];
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.categoryName.toLowerCase().includes(query)
      );
    }
    
    // Override filter
    if (filterOverridden === 'overridden') {
      result = result.filter(item => item.hasOverride);
    } else if (filterOverridden === 'no-override') {
      result = result.filter(item => !item.hasOverride);
    }
    
    setFilteredItems(result);
  }, [items, searchQuery, filterOverridden]);

  // Format price
  const formatPrice = (cents) => {
    if (cents === null || cents === undefined) return '-';
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Open override dialog
  const openOverrideDialog = (item) => {
    setSelectedItem(item);
    setOverrideForm({
      priceOverrideCents: item.override?.priceOverrideCents ?? null,
      isAvailableOverride: item.override?.isAvailableOverride ?? null
    });
    setShowOverrideDialog(true);
  };

  // Save override
  const saveOverride = async () => {
    if (!selectedItem || !selectedBranch) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/menu/items/${selectedItem.id}/override`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({
          branchId: selectedBranch.id,
          priceOverrideCents: overrideForm.priceOverrideCents,
          isAvailableOverride: overrideForm.isAvailableOverride
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Failed to save override');
      
      toast.success(data.message);
      setShowOverrideDialog(false);
      fetchItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Clear override
  const clearOverride = async (item) => {
    if (!selectedBranch) return;
    
    try {
      const res = await fetch(
        `${API}/api/menu/items/${item.id}/override?branchId=${selectedBranch.id}`,
        { method: 'DELETE', headers: getHeaders() }
      );
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to clear override');
      }
      
      toast.success('Override cleared');
      fetchItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Quick toggle availability
  const toggleAvailability = async (item) => {
    const newAvailability = !item.effectiveIsAvailable;
    
    try {
      const res = await fetch(`${API}/api/menu/items/${item.id}/override`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({
          branchId: selectedBranch.id,
          isAvailableOverride: newAvailability,
          priceOverrideCents: item.override?.priceOverrideCents
        })
      });
      
      if (!res.ok) throw new Error('Failed to update');
      
      toast.success(newAvailability ? 'Item available' : 'Item marked as 86\'d');
      fetchItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Bulk 86 selected items
  const bulk86Items = async () => {
    if (selectedIds.size === 0 || !selectedBranch) return;
    
    try {
      const res = await fetch(`${API}/api/menu/overrides/bulk`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          branchId: selectedBranch.id,
          updates: Array.from(selectedIds).map(itemId => ({
            itemId,
            isAvailableOverride: false
          }))
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Failed to update');
      
      toast.success(`Marked ${data.updated + data.created} items as 86'd`);
      setSelectedIds(new Set());
      setBulkMode(false);
      fetchItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Stats
  const overriddenCount = items.filter(i => i.hasOverride).length;
  const unavailableCount = items.filter(i => !i.effectiveIsAvailable).length;

  if (loading && !selectedBranch) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="menu-overrides-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Menu Overrides</h1>
          <p className="text-gray-500">Customize prices and availability per location</p>
        </div>
        
        {/* Branch Selector */}
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-gray-400" />
          <Select 
            value={selectedBranch?.id?.toString()} 
            onValueChange={(val) => {
              const branch = branches.find(b => b.id.toString() === val);
              if (branch) {
                setSelectedBranch(branch);
                localStorage.setItem('current_branch_id', branch.id.toString());
              }
            }}
          >
            <SelectTrigger className="w-[200px]" data-testid="branch-select">
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.map(branch => (
                <SelectItem key={branch.id} value={branch.id.toString()}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{items.length}</p>
              <p className="text-sm text-muted-foreground">Total Items</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{overriddenCount}</p>
              <p className="text-sm text-muted-foreground">Overridden</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">{unavailableCount}</p>
              <p className="text-sm text-muted-foreground">86'd Items</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{items.length - unavailableCount}</p>
              <p className="text-sm text-muted-foreground">Available</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search items or categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Select value={filterOverridden} onValueChange={setFilterOverridden}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Items</SelectItem>
                <SelectItem value="overridden">Overridden Only</SelectItem>
                <SelectItem value="no-override">No Override</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant={bulkMode ? "default" : "outline"}
              onClick={() => {
                setBulkMode(!bulkMode);
                setSelectedIds(new Set());
              }}
            >
              {bulkMode ? 'Cancel Bulk' : 'Bulk Edit'}
            </Button>
            
            {bulkMode && selectedIds.size > 0 && (
              <Button 
                variant="destructive" 
                onClick={bulk86Items}
                data-testid="bulk-86-btn"
              >
                86 Selected ({selectedIds.size})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {bulkMode && <TableHead className="w-12"></TableHead>}
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Base Price</TableHead>
                  <TableHead className="text-right">Effective Price</TableHead>
                  <TableHead className="text-center">Available</TableHead>
                  <TableHead className="text-center">Override</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map(item => (
                  <TableRow 
                    key={item.id}
                    className={!item.effectiveIsAvailable ? 'bg-red-50' : ''}
                    data-testid={`item-row-${item.id}`}
                  >
                    {bulkMode && (
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedIds);
                            if (checked) {
                              newSet.add(item.id);
                            } else {
                              newSet.delete(item.id);
                            }
                            setSelectedIds(newSet);
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {item.imageUrl && (
                          <img 
                            src={item.imageUrl} 
                            alt={item.name}
                            className="w-10 h-10 rounded object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.categoryName}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatPrice(item.basePriceCents)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={
                        item.effectivePriceCents !== item.basePriceCents 
                          ? 'text-blue-600 font-bold' 
                          : ''
                      }>
                        {formatPrice(item.effectivePriceCents)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <button
                        onClick={() => toggleAvailability(item)}
                        className="focus:outline-none"
                        data-testid={`toggle-avail-${item.id}`}
                      >
                        {item.effectiveIsAvailable ? (
                          <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 mx-auto" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      {item.hasOverride ? (
                        <Badge className="bg-blue-100 text-blue-700">Overridden</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openOverrideDialog(item)}
                          data-testid={`edit-${item.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {item.hasOverride && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => clearOverride(item)}
                            className="text-red-600"
                            data-testid={`clear-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {!loading && filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Tag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No menu items found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Override</DialogTitle>
            <DialogDescription>
              Customize this item for {selectedBranch?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6 py-4">
              {/* Item Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedItem.name}</p>
                <p className="text-sm text-muted-foreground">{selectedItem.categoryName}</p>
                <p className="text-sm mt-1">
                  Base price: <span className="font-mono">{formatPrice(selectedItem.basePriceCents)}</span>
                </p>
              </div>
              
              {/* Price Override */}
              <div className="space-y-2">
                <Label>Price Override</Label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Leave empty for base price"
                    value={overrideForm.priceOverrideCents !== null 
                      ? (overrideForm.priceOverrideCents / 100).toFixed(2) 
                      : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setOverrideForm(prev => ({
                        ...prev,
                        priceOverrideCents: val ? Math.round(parseFloat(val) * 100) : null
                      }));
                    }}
                    data-testid="price-input"
                  />
                  {overrideForm.priceOverrideCents !== null && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOverrideForm(prev => ({ ...prev, priceOverrideCents: null }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave empty to use base price ({formatPrice(selectedItem.basePriceCents)})
                </p>
              </div>
              
              {/* Availability Override */}
              <div className="space-y-2">
                <Label>Availability</Label>
                <div className="flex items-center gap-4">
                  <Button
                    variant={overrideForm.isAvailableOverride === true ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOverrideForm(prev => ({ ...prev, isAvailableOverride: true }))}
                    className={overrideForm.isAvailableOverride === true ? 'bg-green-600' : ''}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Available
                  </Button>
                  <Button
                    variant={overrideForm.isAvailableOverride === false ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOverrideForm(prev => ({ ...prev, isAvailableOverride: false }))}
                    className={overrideForm.isAvailableOverride === false ? 'bg-red-600' : ''}
                  >
                    <X className="w-4 h-4 mr-1" />
                    86'd
                  </Button>
                  <Button
                    variant={overrideForm.isAvailableOverride === null ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setOverrideForm(prev => ({ ...prev, isAvailableOverride: null }))}
                  >
                    Use Base
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Base setting: {selectedItem.baseIsAvailable ? 'Available' : '86\'d'}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveOverride} disabled={saving} data-testid="save-override-btn">
              {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Save Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
